import React from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import PageWrapper from "../../../../components/PageWrapper";
import config from "../../../../config";
import { EcommerceEventList } from "../../../../models/Event";
import {
    displayDateRange,
    getImageUrl,
    setupStyleMapFromStyleMap,
} from "../../../../utils/helpers";
import { Container, Box, Grid, Button } from "@mui/material";
import Link from "next/link";
import { useCartStore } from "../../../../stores/CartStore";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import dynamic from "next/dynamic";
import Cart from "../../../../components/cart/Cart";
import { COOKIE_BOXOFFICE_KEY } from "../../../../stores/AuthStore";
import { tokenIsValid } from "../../../../utils/jwtHelpers";
import NothingHereYet from "../../../../components/NothingHereYet";

const DepartmentList = dynamic(
    () => import("../../../../components/event-list/DepartmentList"),
    { ssr: false },
);

export default function SpecialPage(props: {
    eventList: EcommerceEventList;
    sdaCode: string;
}) {
    const cartStore = useCartStore(`SDA-${props.sdaCode}`);

    const baseUrl = `/store/special/${props.sdaCode}`;

    const adminUrl = `/store/${props.eventList.cartUrl}/corporate-dashboard/${props.sdaCode}`;

    React.useEffect(() => {
        if (props.eventList.newStyleMap) {
            setupStyleMapFromStyleMap(props.eventList.newStyleMap);
        }
    }, []);

    const pageTitle = `${props.eventList.clientName} Tickets | AuthentiGATE`;

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="robots" content="noindex" />
            </Head>
            <PageWrapper eventList={props.eventList} baseUrl={baseUrl}>
                <Container maxWidth="xl">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box
                                dangerouslySetInnerHTML={{
                                    __html:
                                        props.eventList.landingPageHtml ?? "",
                                }}
                                sx={{ textAlign: "center" }}
                            />
                        </Grid>
                        {props.eventList.events.length === 0 && (
                            <Grid item xs={12}>
                                <NothingHereYet message="No events currently on sale." />
                            </Grid>
                        )}
                        {props.eventList.events.map((event) => {
                            const venue = event.venueConfiguration.venue;
                            return (
                                <>
                                    <Grid item xs={12}>
                                        <Grid container spacing={2}>
                                            <Grid
                                                item
                                                xs={12}
                                                md={8}
                                                order={{ xs: 2, md: 1 }}
                                                key={event.departments
                                                    .map((it) => it.id)
                                                    .join("")}
                                            >
                                                <DepartmentList
                                                    eventList={{
                                                        ...props.eventList,
                                                        event,
                                                    }}
                                                    cartStore={cartStore}
                                                    showAll
                                                />
                                            </Grid>
                                            <Grid
                                                item
                                                xs={12}
                                                md={4}
                                                order={{ xs: 1, md: 2 }}
                                                key={event.id}
                                            >
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h5">
                                                            Event Details
                                                        </Typography>
                                                        {event.showEventDates && (
                                                            <Typography
                                                                fontWeight={600}
                                                                sx={{ my: 1 }}
                                                            >
                                                                {displayDateRange(
                                                                    event.eventFrom,
                                                                    event.eventTo,
                                                                    true,
                                                                )}
                                                            </Typography>
                                                        )}
                                                        <Box
                                                            component="div"
                                                            dangerouslySetInnerHTML={{
                                                                __html:
                                                                    event?.details ??
                                                                    "",
                                                            }}
                                                        />
                                                        <Typography variant="h5">
                                                            Venue Details
                                                        </Typography>
                                                        <Typography>
                                                            {venue.name}
                                                            <br />
                                                            {
                                                                venue.address
                                                            }, {venue.city},{" "}
                                                            {venue.state}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </>
                            );
                        })}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                        }}
                    >
                        <Link href={adminUrl}>
                            <Button variant="text" size="small">
                                Admin Login
                            </Button>
                        </Link>
                    </Grid>
                </Container>
            </PageWrapper>
            <Cart baseUrl={baseUrl} />
        </>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const sdaCode = context.params?.sdaCode;
    let data: any,
        error = false;

    const boxoffice = context.req.cookies[COOKIE_BOXOFFICE_KEY];
    let salesContext = "ECOMMERCE";

    if (
        boxoffice == "true" &&
        tokenIsValid(
            context.req.cookies[
                config.COOKIE_PREFIX + "__authentigate_token_sanitized"
            ],
        )
    ) {
        salesContext = "BOXOFFICE";
    }

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/special-events/${sdaCode}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": salesContext,
                    Cookie: context.req.headers.cookie ?? "",
                },
                credentials: "include",
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(res.status, res.statusText);
            console.log(await res.text());
        } else {
            data = await res.json();
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    if (data?.newStyleMap) {
        setupStyleMapFromStyleMap(data.newStyleMap);
    }

    return {
        props: { eventList: data, sdaCode },
        notFound: error,
    };
};
